import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { listKafJournal, saveKafJournalEntry, deleteKafJournalEntry } from "@/lib/kaf-academy.functions";
import { LivingBackdrop } from "@/academy/engines/living/LivingBackdrop";
import { Trash2 } from "lucide-react";
import { useState } from "react";

export const Route = createFileRoute("/_authenticated/academy/kaf/journal")({
  head: () => ({ meta: [{ title: "Journal — KAfrequency" }] }),
  component: JournalPage,
});

function JournalPage() {
  const qc = useQueryClient();
  const listFn = useServerFn(listKafJournal);
  const saveFn = useServerFn(saveKafJournalEntry);
  const delFn = useServerFn(deleteKafJournalEntry);
  const q = useQuery({ queryKey: ["kaf", "journal"], queryFn: () => listFn() });
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [mood, setMood] = useState("");
  const [query, setQuery] = useState("");

  const save = useMutation({
    mutationFn: () => saveFn({ data: { title, body, mood } }),
    onSuccess: () => {
      setTitle(""); setBody(""); setMood("");
      qc.invalidateQueries({ queryKey: ["kaf", "journal"] });
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => delFn({ data: { id } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["kaf", "journal"] }),
  });

  const entries = (q.data ?? []).filter((e) =>
    !query || (e.title ?? "").toLowerCase().includes(query.toLowerCase()) || e.body.toLowerCase().includes(query.toLowerCase()),
  );

  return (
    <div className="relative min-h-dvh bg-obsidian text-foreground">
      <div className="absolute inset-0 opacity-60"><LivingBackdrop hue={260} density={70} /></div>
      <section className="relative z-10 mx-auto max-w-4xl px-4 py-12 sm:px-6">
        <p className="text-xs uppercase tracking-[0.3em] text-gold">Private Journal</p>
        <h1 className="mt-2 font-serif text-4xl text-gold-luminous">The Inner Log</h1>

        <div className="mt-8 rounded-2xl border hairline bg-obsidian/70 p-5">
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title (optional)"
            className="w-full bg-transparent text-lg font-serif text-gold-luminous outline-none placeholder:text-foreground/40" />
          <textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Write freely…"
            className="mt-3 min-h-[160px] w-full bg-transparent text-sm outline-none placeholder:text-foreground/40" />
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <input value={mood} onChange={(e) => setMood(e.target.value)} placeholder="Mood (calm, alive, tender…)"
              className="rounded-md border hairline bg-obsidian/60 px-3 py-2 text-sm outline-none focus:border-gold/60" />
            <button onClick={() => save.mutate()} disabled={!body.trim() || save.isPending}
              className="rounded-full bg-gold-gradient px-5 py-2 text-sm font-semibold text-obsidian disabled:opacity-60">
              {save.isPending ? "Sealing…" : "Save entry"}
            </button>
          </div>
        </div>

        <div className="mt-10">
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search entries…"
            className="w-full rounded-md border hairline bg-obsidian/60 px-3 py-2 text-sm outline-none focus:border-gold/60" />
          <div className="mt-4 flex flex-col gap-3">
            {entries.map((e) => (
              <div key={e.id} className="rounded-xl border hairline bg-obsidian/50 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-foreground/50">
                      {new Date(e.created_at).toLocaleDateString()} {e.mood && `· ${e.mood}`}
                    </div>
                    {e.title && <div className="mt-1 font-serif text-lg text-gold-luminous">{e.title}</div>}
                  </div>
                  <button onClick={() => remove.mutate(e.id)} className="text-foreground/40 hover:text-red-400" aria-label="Delete">
                    <Trash2 size={14} />
                  </button>
                </div>
                <p className="mt-2 whitespace-pre-wrap text-sm text-foreground/85">{e.body}</p>
              </div>
            ))}
            {!entries.length && !q.isLoading && (
              <p className="text-sm text-foreground/50">No entries yet. The first line begins the practice.</p>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
